/**
 * useTegan.js
 * ===========
 * Central data hook for all Tegan bridge communication.
 * Manifest is fetched via HTTP GET /manifest.
 * Commands and responses flow over WebSocket.
 */

import { useState, useEffect, useRef, useCallback } from 'react';

const BRIDGE_URL = (process.env.REACT_APP_BRIDGE_URL || 'https://tegan.bridgepath.us').replace(/\/$/, '');
const BRIDGE_KEY = process.env.REACT_APP_BRIDGE_KEY || '';

const WS_BASE = BRIDGE_URL.replace(/^https?:\/\//, match =>
  match.startsWith('https') ? 'wss://' : 'ws://'
);
const WS_URL       = `${WS_BASE}/ws?key=${encodeURIComponent(BRIDGE_KEY)}`;
const MANIFEST_URL = `${BRIDGE_URL}/manifest`;

const BACKOFF = [1000, 2000, 4000, 8000, 16000, 30000];

export function useTegan() {
  const [manifest,         setManifest]  = useState([]);
  const [messages,         setMessages]  = useState(() => {
    try {
      const stored = localStorage.getItem('tegan_messages');
      return stored ? JSON.parse(stored) : [];
    } catch { return []; }
  });
  const [connectionStatus, setStatus]    = useState('connecting');
  const [metrics,          setMetrics]   = useState({ cpu: 0, ram: 0, drive: 0 });

  const wsRef              = useRef(null);
  const reconnectTimer     = useRef(null);
  const reconnectAttempt   = useRef(0);
  const unmounted          = useRef(false);
  const metricsIntervalRef = useRef(null);

  // ── Persist messages to localStorage (keep last 200) ─────────────────────
  useEffect(() => {
    try {
      const trimmed = messages.slice(-200);
      localStorage.setItem('tegan_messages', JSON.stringify(trimmed));
    } catch (err) {
      console.warn('[useTegan] localStorage write failed:', err);
    }
  }, [messages]);

  const clearHistory = useCallback(() => {
    setMessages([]);
    localStorage.removeItem('tegan_messages');
  }, []);

  // ── Manifest fetch via HTTP — immune to WebSocket timing issues ──────────
  useEffect(() => {
    let cancelled = false;
    fetch(MANIFEST_URL, { headers: { 'X-Tegan-Key': BRIDGE_KEY } })
      .then(r => { if (!r.ok) throw new Error(`/manifest ${r.status}`); return r.json(); })
      .then(data => { if (!cancelled) setManifest(Array.isArray(data) ? data : []); })
      .catch(err => { console.error('[useTegan] manifest fetch error:', err); });
    return () => { cancelled = true; };
  }, []);

  // ── WebSocket for commands + responses + metrics ─────────────────────────
  const connect = useCallback(() => {
    if (unmounted.current) return;
    setStatus('connecting');

    const ws = new WebSocket(WS_URL);
    wsRef.current = ws;

    ws.onopen = () => {
      if (unmounted.current) { ws.close(); return; }
      reconnectAttempt.current = 0;
      setStatus('connected');

      // Request metrics immediately — use a generous delay for Cloudflare
      setTimeout(() => {
        if (ws.readyState === WebSocket.OPEN && !unmounted.current) {
          ws.send('__metrics__');
        }
      }, 500);

      // Poll metrics every 5 seconds
      const metricsInterval = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) ws.send('__metrics__');
      }, 5000);
      metricsIntervalRef.current = metricsInterval;
    };

    ws.onmessage = (event) => {
      try {
        const frame = JSON.parse(event.data);
        if (frame.type === 'metrics') {
          setMetrics({ cpu: frame.cpu, ram: frame.ram, drive: frame.drive });
        }
        if (frame.type === 'response' && typeof frame.text === 'string') {
          setMessages(prev => [...prev, { role: 'tegan', text: frame.text, timestamp: Date.now() }]);
        }
      } catch (err) {
        console.warn('[useTegan] non-JSON frame:', event.data);
      }
    };

    ws.onerror = () => { setStatus('error'); };

    ws.onclose = () => {
      if (unmounted.current) return;
      clearInterval(metricsIntervalRef.current);
      setStatus('disconnected');
      wsRef.current = null;
      const delay = BACKOFF[Math.min(reconnectAttempt.current, BACKOFF.length - 1)];
      reconnectAttempt.current += 1;
      reconnectTimer.current = setTimeout(connect, delay);
    };
  }, []);

  useEffect(() => {
    unmounted.current = false;
    connect();
    return () => {
      unmounted.current = true;
      clearTimeout(reconnectTimer.current);
      clearInterval(metricsIntervalRef.current);
      if (wsRef.current) {
        wsRef.current.onclose = null;
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [connect]);

  const sendCommand = useCallback((text) => {
    const trimmed = (text || '').trim();
    if (!trimmed) return false;
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      console.warn('[useTegan] sendCommand: WebSocket not open');
      return false;
    }
    setMessages(prev => [...prev, { role: 'user', text: trimmed, timestamp: Date.now() }]);
    ws.send(trimmed);
    return true;
  }, []);

  return { manifest, connected: connectionStatus === 'connected', messages, sendCommand, connectionStatus, metrics, clearHistory };
}
